/* eslint-disable max-len */
import { useMutation } from '@apollo/client';
import React, { useEffect, useMemo, useState } from 'react';
import AdminModal from '../../../components/AdminModal';
import TextInput from '../../../components/input';
import SelectSearch from '../../../components/SelectSearch';
import useToast from '../../../customHooks/useToast';
import { UPDATE_KAABO_COUPON } from '../../../graphql/admin_mutations';
import { GET_KAABO_PARTNERS } from '../../../graphql/admin_queries';
import useCustomQuery from '../../../network/useCustomQuery';
import InfoIconNew from '../../../svgs/InfoIconNew';
import Styles from '../PartnerCoupons.module.scss';

interface IEditCouponModal {
  coupon: any;
  closeModal: () => void;
  refetch: () => void;
}

const toLocalInput = (iso?: string) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
};

const EditCouponModal = ({ coupon, closeModal, refetch }: IEditCouponModal) => {
  const Toast = useToast();
  const [couponAlias, setcouponAlias] = useState(coupon?.couponAlias || '');
  const [partnerId, setPartnerId] = useState(coupon?.partnerId || '');
  const [discountType, setDiscountType] = useState(
    coupon?.discountType || 'percentage',
  );
  const [discountValue, setDiscountValue] = useState(
    String(coupon?.discountValue ?? ''),
  );
  const [maxUses, setMaxUses] = useState(String(coupon?.maxUses ?? ''));
  const [expiryLocal, setExpiryLocal] = useState(
    toLocalInput(coupon?.expiryDate),
  );
  const [success, setSuccess] = useState(false);

  const [getPartners, , partnersData] = useCustomQuery({
    query: GET_KAABO_PARTNERS,
    onError: () => {},
    onData: () => {},
  });

  useEffect(() => {
    getPartners({ variables: { offset: 0, limit: 500, search: '' } });
  }, []);

  const partnerOptions = useMemo(() => {
    const list = partnersData?.getKaaboPartners?.data?.kaaboPartners || [];
    return list.map((p: any) => ({
      label: `${p.partnerName || 'Partner'} (${p.email || p.id})`,
      value: p.id,
    }));
  }, [partnersData]);

  const [updateCoupon, { loading }] = useMutation(UPDATE_KAABO_COUPON);

  const handleSubmit = async () => {
    if (!expiryLocal) {
      Toast.error('Select an expiry date and time');
      return;
    }
    const dv = Number(discountValue);
    if (Number.isNaN(dv) || dv < 0) {
      Toast.error('Enter a valid discount value');
      return;
    }
    const mu = Number(maxUses);
    if (Number.isNaN(mu) || mu < 1) {
      Toast.error('Max uses must be at least 1');
      return;
    }
    const expiryDate = new Date(expiryLocal).toISOString();
    try {
      const res = await updateCoupon({
        variables: {
          couponId: coupon.id,
          input: {
            couponAlias: couponAlias.trim() || null,
            partnerId: partnerId || null,
            expiryDate,
            maxUses: mu,
            discountType,
            discountValue: dv,
          },
        },
      });
      if (res.data?.updateCoupon?.status === 200) {
        setSuccess(true);
        refetch();
      } else {
        Toast.error(res.data?.updateCoupon?.message || 'Update failed');
      }
    } catch (e: any) {
      Toast.error(e?.graphQLErrors?.[0]?.message || 'Something went wrong');
    }
  };

  return (
    <div className={Styles.modalFields}>
      <AdminModal
        headText={success ? 'Coupon updated' : 'Edit coupon'}
        subText={
          success ? (
            <span>Changes are saved. The redeemable code was not changed.</span>
          ) : (
            <span>
              Update expiry, discount, partner, or tag. Coupon code stays the
              same.
            </span>
          )
        }
        actionBtnName={success ? 'Done' : 'Save changes'}
        handleModalAction={success ? closeModal : handleSubmit}
        topIcon={<InfoIconNew />}
        isShow
        isSuccess={success}
        isActionBtnDisabled={loading}
        onClickAway={() => {}}
        onClose={closeModal}
        isLoading={loading}
      >
        {!success && (
          <div className={Styles.modalFields}>
            <div>
              <div style={{ fontSize: 12, marginBottom: 6 }}>Coupon code</div>
              <div className={Styles.readonlyCode}>{coupon?.couponCode}</div>
            </div>
            <TextInput
              name="couponAlias"
              value={couponAlias}
              label="Tag / alias"
              customOnChange={(e: any) => setcouponAlias(e.target.value)}
              backgroundColor="rgba(255, 255, 255, 1)"
            />
            <SelectSearch
              label=""
              options={partnerOptions}
              placeholder="Partner (optional)"
              name="partner"
              value={partnerId}
              classes={false}
              hideLabel
              fullWidth
              onChange={(e: any) => setPartnerId(e?.target?.value || '')}
              disabled={false}
            />
            <SelectSearch
              label=""
              options={[
                { label: 'Percentage off', value: 'percentage' },
                { label: 'Fixed amount', value: 'fixed' },
              ]}
              placeholder="Discount type"
              name="discountType"
              value={discountType}
              classes={false}
              isRequired
              hideLabel
              fullWidth
              onChange={(e: any) => setDiscountType(e?.target?.value)}
              disabled={false}
            />
            <TextInput
              name="discountValue"
              value={discountValue}
              label={
                discountType === 'percentage' ? 'Discount %' : 'Discount amount'
              }
              customOnChange={(e: any) => setDiscountValue(e.target.value)}
              backgroundColor="rgba(255, 255, 255, 1)"
            />
            <TextInput
              name="maxUses"
              value={maxUses}
              label="Max redemptions"
              customOnChange={(e: any) => setMaxUses(e.target.value)}
              backgroundColor="rgba(255, 255, 255, 1)"
            />
            <label
              htmlFor="editExpiryLocal"
              style={{ fontSize: 12, display: 'block' }}
            >
              <span style={{ display: 'block', marginBottom: 6 }}>
                Expires
                <span style={{ color: '#c00' }}>*</span>
              </span>
              <input
                id="editExpiryLocal"
                type="datetime-local"
                value={expiryLocal}
                onChange={(e) => setExpiryLocal(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: '1px solid rgba(226, 232, 240, 1)',
                  fontSize: 14,
                }}
              />
            </label>
          </div>
        )}
      </AdminModal>
    </div>
  );
};

export default EditCouponModal;
